import { useEffect, useMemo, useRef, useState } from "react";
import { RadioGroup } from "@headlessui/react";
import { t } from "@/i18n-js/instance";
import { isDowngrading, planTiers } from "@/react/helpers/planHelpers";
import { usePlanPageVariant } from "@/react/hooks/usePlanPageVariant";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import type { Plan } from "@/react/types/Plan";
import { usePlans } from "@circle-react/components/SettingsApp/Plan/RegularPlan/PlansContext";
import { Icon } from "@circle-react-shared/Icon";
import { SkeletonLoader } from "@circle-react-shared/uikit/SkeletonLoader";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { PlanSelectorTitle } from "../experiment/PlanSelectorTitle";
import { PlanCard } from "./PlanCard";

const localeRoot = "pricing_plans.plan_settings.plan_selection.plan_selector";

export const PlanSelector = () => {
  const { plans, selectedPlan, setSelectedPlanTier } = usePlans();
  const { isCommunityOnPaidPlan, currentPlanTier, isCommunityOnTrial } =
    usePlanStatus();
  const [areAllPlansVisible, setAreAllPlansVisible] = useState(false);

  const hiddenPlansRef = useRef<HTMLDivElement>(null);
  const [hiddenPlansHeight, setHiddenPlansHeight] = useState<number>(0);
  const { isDesktopLayout } = usePlanPageVariant();

  const sortedPlans = useMemo(() => [...plans].reverse(), [plans]);

  const { visiblePlans, hiddenPlans } = useMemo(() => {
    if (areAllPlansVisible) {
      return {
        visiblePlans: sortedPlans,
        hiddenPlans: [],
      };
    }

    const isHidden = (plan: Plan) => {
      if (isCommunityOnTrial) return plan.tier === planTiers.basic;
      if (isCommunityOnPaidPlan && currentPlanTier)
        return isDowngrading(currentPlanTier, plan.tier);
      return false;
    };

    return {
      visiblePlans: sortedPlans.filter(plan => !isHidden(plan)),
      hiddenPlans: sortedPlans.filter(plan => isHidden(plan)),
    };
  }, [
    sortedPlans,
    areAllPlansVisible,
    isCommunityOnTrial,
    isCommunityOnPaidPlan,
    currentPlanTier,
  ]);

  useEffect(() => {
    if (areAllPlansVisible && hiddenPlansRef.current) {
      setHiddenPlansHeight(hiddenPlansRef.current.scrollHeight);
    } else {
      setHiddenPlansHeight(0);
    }
  }, [areAllPlansVisible, hiddenPlans.length]);

  if (plans.length === 0) {
    return <SkeletonLoader variant="plan-selector" cardCount={3} />;
  }

  const toggleText = areAllPlansVisible ? "see_less_plans" : "see_more_plans";

  return (
    <RadioGroup value={selectedPlan?.id}>
      <div className="flex w-full justify-center">
        <ul className="border-primary flex w-full flex-col overflow-hidden rounded-2xl border">
          {isDesktopLayout && <PlanSelectorTitle />}

          {visiblePlans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              selected={plan.tier === selectedPlan?.tier}
              onClick={() => setSelectedPlanTier(plan.tier)}
            />
          ))}

          {hiddenPlans.length > 0 && (
            <div
              ref={hiddenPlansRef}
              className="overflow-hidden duration-300 ease-in-out"
              style={{ height: `${hiddenPlansHeight}px` }}
            >
              {hiddenPlans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={plan.tier === selectedPlan?.tier}
                  onClick={() => setSelectedPlanTier(plan.tier)}
                />
              ))}
            </div>
          )}

          {hiddenPlans.length > 0 && (
            <button
              type="button"
              className="bg-primary flex w-full cursor-pointer flex-row justify-start gap-0 px-6 py-4"
              onClick={() => setAreAllPlansVisible(!areAllPlansVisible)}
            >
              <Typography.BodyXs weight="font-medium">
                {t(`${localeRoot}.${toggleText}`)}
              </Typography.BodyXs>
              <Icon
                type={areAllPlansVisible ? "16-chevron-up" : "16-chevron-down"}
                size={12}
                className="ml-2"
              />
            </button>
          )}
        </ul>
      </div>
    </RadioGroup>
  );
};
